import { useContext, useEffect, useMemo, useState } from "react";
import CustomSpinner from "../components/CustomSpinner";
import QRScannerModal from "../components/QRScannerModal";
import UserContext from "../context/UserContext";
import deliveryService from "../services/deliveryService";

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const dateTime = (value) => new Date(value).toLocaleString("es-MX", { timeZone: "America/Mexico_City" });

export default function Delivery() {
  const { user } = useContext(UserContext);
  const [sales, setSales] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [sale, setSale] = useState(null);
  const [scanned, setScanned] = useState([]);
  const [code, setCode] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [evidenceDelivery, setEvidenceDelivery] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [salesResponse, deliveriesResponse] = await Promise.all([
        deliveryService.getSales(),
        deliveryService.getMine(),
      ]);
      setSales(salesResponse.data);
      setDeliveries(deliveriesResponse.data);
    } catch (error) {
      alert(error.response?.data?.message || "No fue posible cargar las entregas.");
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const productCodes = useMemo(() => sale?.products.map((product) => product.folio) || [], [sale]);
  const allScanned = productCodes.length > 0 && productCodes.every((folio) => scanned.includes(folio));
  const total = sale?.products.reduce((sum, product) => sum + product.price * product.quantity, 0) || 0;

  const selectSale = async (saleId) => {
    setLoading(true);
    try {
      const { data } = await deliveryService.getSale(saleId);
      setSale(data);
      setScanned([]);
      setCode("");
    } catch (error) {
      alert(error.response?.data?.message || "No fue posible cargar la factura.");
    } finally {
      setLoading(false);
    }
  };

  const registerCode = (value) => {
    if (!value) return;
    if (!productCodes.includes(value)) alert("El código no pertenece a esta venta.");
    else if (scanned.includes(value)) alert("Este producto ya fue escaneado.");
    else setScanned([...scanned, value]);
    setCode("");
  };

  const scanCode = (event) => {
    if (event.key === "Enter") registerCode(code.trim());
  };

  const startDelivery = async () => {
    setLoading(true);
    try {
      await deliveryService.start(sale.id);
      setSale(null);
      await load();
      alert("Entrega iniciada correctamente.");
    } catch (error) {
      alert(error.response?.data?.message || "No fue posible iniciar la entrega.");
    } finally {
      setLoading(false);
    }
  };

  const completeDelivery = async () => {
    if (!file) return alert("Selecciona una foto como evidencia.");
    setLoading(true);
    try {
      await deliveryService.complete(evidenceDelivery.id, file);
      setEvidenceDelivery(null);
      setFile(null);
      await load();
      alert("Entrega marcada como entregada.");
    } catch (error) {
      alert(error.response?.data?.message || "No fue posible guardar la evidencia.");
    } finally {
      setLoading(false);
    }
  };

  return <>
    {loading && <CustomSpinner />}
    <div className="app-shell banner-1"><div className="app-panel">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Reparto</h1>
        <span className="text-sm">Repartidor(a): <strong>{user?.name || user?.user}</strong></span>
      </div>

      <section className="text-left">
        <h2 className="mb-3 text-xl font-semibold">Ventas por entregar</h2>
        <div className="overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10">
          <table className="w-full text-sm"><thead><tr>
            <th className="p-3 text-left">Venta</th>
            <th className="p-3 text-left">Fecha</th>
            <th className="p-3 text-left">Vendedor</th>
            <th className="p-3 text-left">Productos</th>
            <th className="p-3 text-right">Total</th>
            <th className="p-3 text-right">Acción</th>
          </tr></thead><tbody>
              {sales.filter(item => item.status !== "entregado").map((item) => <tr key={item.id} className="border-t border-black/10 dark:border-white/10">
                <td className="p-3">#{item.id}</td>
                <td className="p-3">{dateTime(item.createdAt)}</td>
                <td className="p-3">{item.vendedor}</td>
                <td className="p-3">{item.products}</td>
                <td className="p-3 text-right">{money(item.price)}</td>
                <td className="p-3 text-right">
                  {item.status ? <span className="text-sm font-medium">{item.status}</span> :
                    <button className="btn btn-info" onClick={() => selectSale(item.id)}>Preparar entrega</button>}
                </td>
              </tr>)}
            </tbody></table>
        </div>
      </section>

      {sale && <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4" role="dialog" aria-modal="true">
        <section className="my-8 w-full max-w-4xl rounded-2xl border border-sky-500/30 bg-slate-50 p-5 text-left shadow-2xl dark:bg-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="text-xl font-semibold">Factura de venta #{sale.id}</h2><p className="text-sm">ALTAPLAS · {dateTime(sale.createdAt)} · {sale.vendedor}</p></div>
          </div>
          <div className="my-5 overflow-x-auto"><table className="w-full text-sm"><thead><tr>
            <th className="p-2 text-left">Producto</th><th className="p-2 text-left">Folio</th><th className="p-2 text-right">Cantidad</th><th className="p-2 text-right">Precio</th><th className="p-2 text-right">Estado</th>
          </tr></thead><tbody>{sale.products.map((product) => <tr key={`${product.productId}-${product.folio}`} className="border-t border-black/10 dark:border-white/10">
            <td className="p-2">{product.productName}</td><td className="p-2">{product.folio}</td><td className="p-2 text-right">{product.quantity}</td><td className="p-2 text-right">{money(product.price * product.quantity)}</td>
            <td className="p-2 text-right">{scanned.includes(product.folio) ? "Confirmado" : "Pendiente"}</td>
          </tr>)}</tbody></table></div>
          <div className="flex flex-wrap items-end justify-between gap-4 border-t border-black/10 pt-4 dark:border-white/10">
            <div className="block text-sm font-medium">Escanear código de producto
              <button className="btn btn-info mt-2 block w-full sm:w-auto" onClick={() => setShowScanner(true)}>📷 Abrir cámara</button>
              <input autoFocus className="form-control mt-2 min-w-64" value={code} onChange={(event) => setCode(event.target.value)} onKeyDown={scanCode} placeholder="Escanea y presiona Enter" />
            </div>
            <div className="text-right">
              <p className="mb-3 text-lg font-semibold">
                Total: {money(total)}
              </p>
              <button className="btn" onClick={() => setSale(null)}>
                Cerrar
                </button>
              <button className="btn btn-success" disabled={!allScanned}
                onClick={startDelivery}>
                Comenzar entrega
              </button>
            </div>
          </div>
        </section>
      </div>}

      <section className="mt-10 text-left"><h2 className="mb-3 text-xl font-semibold">Mis entregas</h2>
        <div className="overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10"><table className="w-full text-sm"><thead><tr>
          <th className="p-3 text-left">Entrega</th><th className="p-3 text-left">Venta</th><th className="p-3 text-left">Inicio</th><th className="p-3 text-left">Productos</th><th className="p-3 text-left">Estado</th><th className="p-3 text-right">Acción</th>
        </tr></thead><tbody>{deliveries.map((item) => <tr key={item.id} className="border-t border-black/10 dark:border-white/10">
          <td className="p-3">#{item.id}</td><td className="p-3">#{item.saleId}</td><td className="p-3">{dateTime(item.createdAt)}</td><td className="p-3">{item.products}</td><td className="p-3">{item.status}</td>
          <td className="p-3 text-right">{item.status === "en proceso de entrega" && <button className="btn btn-success" onClick={() => setEvidenceDelivery(item)}>Marcar entregado</button>}</td>
        </tr>)}</tbody></table></div>
      </section>
    </div></div>

    {evidenceDelivery && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-slate-50 p-6 shadow-2xl dark:bg-slate-900"><h2 className="text-xl font-semibold">Evidencia de entrega #{evidenceDelivery.id}</h2>
        <p className="mt-2 text-sm">Selecciona una foto para confirmar que la venta fue entregada.</p><input className="form-control mt-4" type="file" accept="image/*" onChange={(event) => setFile(event.target.files[0] || null)} />
        <div className="mt-6 flex justify-end gap-2"><button className="btn" onClick={() => { setEvidenceDelivery(null); setFile(null); }}>Cancelar</button><button className="btn btn-success" onClick={completeDelivery}>Guardar evidencia</button></div>
      </div>
    </div>}
    {showScanner && <QRScannerModal onDetected={(value) => { setShowScanner(false); registerCode(value); }} onClose={() => setShowScanner(false)} />}
  </>;
}
